export interface StrongToken {
  type: "strong";
  code: string;
  label: string;
}

type VerseToken = string | StrongToken;

/**
 * Tokenises raw verse text: strips paragraph (<CM>), formatting (<FI>/<FO>)
 * and footnote (<RF>…</RF>) markup, and turns Strong references
 * (<WH4428>, <WG3056>, <XWH4438>, …) into interactive tokens. With
 * `hasStrong === false` the Strong markup is removed entirely.
 */
export function formatVerseText(text: string, hasStrong = true): VerseToken[] {
  const cleanedText = text
    .replaceAll(/<CM>/gi, "")
    .replaceAll(/<RF>.*?<\\?\/RF>/gi, "")
    .replaceAll(/<\\?\/?[RFrf]+>/g, "")
    .replaceAll(/<(FI|FO)>/gi, "")
    .replaceAll(/<\/(FI|FO)>/gi, "");

  if (!hasStrong) {
    return [cleanedText.replaceAll(/<X?W[GH]\d+>/gi, "")];
  }

  const parts: VerseToken[] = [];
  const regex = /<X?W([GH])(\d+)>/gi;
  let lastIndex = 0;
  let match = regex.exec(cleanedText);

  while (match !== null) {
    const matchIndex = match.index;
    if (matchIndex > lastIndex) {
      parts.push(cleanedText.slice(lastIndex, matchIndex));
    }
    const [, prefix, number] = match;
    if (prefix && number) {
      const code = `${prefix.toUpperCase()}${number}`;
      parts.push({
        type: "strong",
        code,
        label: code,
      });
    }
    ({ lastIndex } = regex);
    match = regex.exec(cleanedText);
  }

  if (lastIndex < cleanedText.length) {
    parts.push(cleanedText.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [cleanedText];
}
